import { Plus, X, Copy } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { DAY_LABELS, validateDaySegments } from "./onboarding-logic";
import type { DayAvailability } from "./types";

interface Props {
  days: DayAvailability[];
  onChange: (days: DayAvailability[]) => void;
}

export function WeeklyAvailabilityEditor({ days, onChange }: Props) {
  const update = (idx: number, patch: Partial<DayAvailability>) =>
    onChange(days.map((d, i) => (i === idx ? { ...d, ...patch } : d)));

  const toggleDay = (idx: number) => {
    const d = days[idx]!;
    if (d.enabled) {
      update(idx, { enabled: false, segments: [] });
    } else {
      update(idx, { enabled: true, segments: [{ startTime: "09:00", endTime: "17:00" }] });
    }
  };

  const addSegment = (idx: number) =>
    update(idx, { segments: [...days[idx]!.segments, { startTime: "09:00", endTime: "17:00" }] });

  const removeSegment = (idx: number, segIdx: number) => {
    const segments = days[idx]!.segments.filter((_, i) => i !== segIdx);
    update(idx, { segments, enabled: segments.length > 0 });
  };

  const editSegment = (idx: number, segIdx: number, field: "startTime" | "endTime", value: string) =>
    update(idx, {
      segments: days[idx]!.segments.map((s, i) => (i === segIdx ? { ...s, [field]: value } : s)),
    });

  const copyToAll = (idx: number) => {
    const src = days[idx]!.segments;
    onChange(days.map((d) => (d.enabled ? { ...d, segments: src.map((s) => ({ ...s })) } : d)));
  };

  return (
    <div className="divide-y divide-[oklch(90%_0.02_84)] overflow-hidden rounded-xl border border-[oklch(90%_0.02_84)] bg-card">
      {days.map((day, idx) => {
        const err = day.enabled ? validateDaySegments(day.segments) : null;
        return (
          <div
            key={day.dayOfWeek}
            className={cn(
              "grid grid-cols-[140px_1fr_auto] items-start gap-4 p-4 max-sm:grid-cols-1 max-sm:gap-3",
              !day.enabled && "bg-muted/20"
            )}
          >
            <div className="flex items-center gap-3 pt-1.5">
              <Checkbox checked={day.enabled} onCheckedChange={() => toggleDay(idx)} />
              <span className={cn("text-sm font-semibold", !day.enabled && "text-muted-foreground")}>
                {DAY_LABELS[idx]}
              </span>
            </div>

            <div className="min-w-0 space-y-2">
              {day.enabled ? (
                <>
                  {day.segments.map((seg, segIdx) => (
                    <div key={segIdx} className="flex items-center gap-2">
                      <Input
                        type="time"
                        value={seg.startTime}
                        onChange={(e) => editSegment(idx, segIdx, "startTime", e.target.value)}
                        className="w-28"
                      />
                      <span className="text-muted-foreground">–</span>
                      <Input
                        type="time"
                        value={seg.endTime}
                        onChange={(e) => editSegment(idx, segIdx, "endTime", e.target.value)}
                        className="w-28"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={() => removeSegment(idx, segIdx)}
                        aria-label="Ukloni period"
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" onClick={() => addSegment(idx)}>
                    <Plus className="size-4" /> Dodaj vremenski period
                  </Button>
                  {err && <p className="text-xs text-rose-600">{err}</p>}
                </>
              ) : (
                <span className="block pt-2 text-sm italic text-muted-foreground">Zatvoreno</span>
              )}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => copyToAll(idx)}
              disabled={!day.enabled}
              title="Kopiraj na sve uključene dane"
              aria-label="Kopiraj na sve uključene dane"
              className={cn(!day.enabled && "invisible")}
            >
              <Copy className="size-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
